import { z } from 'zod'

import type { GridColumn } from '@/components/grid'
import { parseDay } from '@/features/cash/manual-item-grid'
import { formatDateOnly, formatDateTime, formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { CashPart, CashPlanBulkRow, CashPlanCari, CashPlanItem } from '@/services/http/cash'

const t = messages.cash.planning
export const CLASSES = ['Ödeme', 'Avans', 'Kontrol', 'Hariç']

/**
 * `/nakit/odeme-planlama` ızgarasının satırı (docs/ODEME-PLANLAMA-IZGARA-PLAN.md Faz 2): bir cari
 * kalem. Düzenlenen alanlar metin/değerdir (kısımlar tek metin, kalan tarihi ISO ya da çözülemeyen ham
 * metin); `item` sunucudaki hâlidir (salt-okunur kolonlar ondan okunur). `version` okunan plan sürümü.
 */
export interface PlanningGridRow {
  key: string
  version: number
  item: CashPlanItem
  cariCode: string
  cariName: string
  klass: string
  erpOutside: boolean
  parts: string
  remainderDate: string
  /** '' = araç seçilmedi (kurala göre). */
  remainderInstrument: string
  note: string
}

/** Sunucu alanı → ızgara kolonu. */
export const PLANNING_GRID_FIELDS: Partial<Record<string, string>> = {
  erp_outside: 'erpOutside',
  remainder_date: 'remainderDate',
  remainder_instrument: 'remainderInstrument',
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const BANK = 'Banka'

function amountText(value: number): string {
  return formatDecimal(value, 2)
}

/** Kısımlar metni: "10.11.2026 50.000,00 Kasa; 01.12.2026 25.000,00"; tarihsiz kısım "?". */
export function partsText(parts: readonly CashPart[]): string {
  return parts
    .map((p) => {
      const date = p.date ? formatDateOnly(p.date) : '?'
      const instrument = p.instrument && p.instrument !== BANK ? ` ${p.instrument}` : ''
      return `${date} ${amountText(p.amount)}${instrument}`
    })
    .join('; ')
}

/**
 * Tutar: "50.000,00" / "50000,5" (Türkçe), "50.000" (binlik ayraçlı) ya da "50000.5"; değilse `NaN`.
 */
export function parsePartAmount(text: string): number {
  const s = text.trim()
  if (!s) return Number.NaN
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.'))
  if (/^\d{1,3}(\.\d{3})+$/.test(s)) return Number(s.replace(/\./g, ''))
  return Number(s)
}

export type ParsedParts =
  | { ok: true; parts: Array<{ date: string | null; amount: number; instrument: string }> }
  | { ok: false; message: string }

/** Kısımlar metnini çözer; araç adı `instruments` içinde büyük/küçük harf duyarsız aranır. */
export function parseParts(text: string, instruments: readonly string[]): ParsedParts {
  const parts: Array<{ date: string | null; amount: number; instrument: string }> = []
  for (const chunk of text.split(';')) {
    const tokens = chunk.trim().split(/\s+/).filter(Boolean)
    if (tokens.length === 0) continue
    if (tokens.length < 2) return { ok: false, message: t.grid.partsInvalid }
    const [dateToken, amountToken, ...rest] = tokens as [string, string, ...string[]]
    const date = dateToken === '?' || dateToken === '-' ? null : parseDay(dateToken)
    if (date === null && dateToken !== '?' && dateToken !== '-') {
      return { ok: false, message: t.grid.partsInvalid }
    }
    const amount = parsePartAmount(amountToken)
    if (!(amount > 0)) return { ok: false, message: t.amountInvalid }
    const name = rest.join(' ')
    const instrument = name
      ? instruments.find((i) => i.toLocaleLowerCase('tr') === name.toLocaleLowerCase('tr'))
      : BANK
    if (!instrument) return { ok: false, message: t.grid.instrumentUnknown(name) }
    parts.push({ date, amount, instrument })
  }
  return { ok: true, parts }
}

export function gridRowFromItem(cari: CashPlanCari, item: CashPlanItem): PlanningGridRow {
  return {
    key: item.key,
    version: item.version ?? 0,
    item,
    cariCode: cari.code,
    cariName: cari.name ?? cari.code,
    klass: item.klass,
    erpOutside: item.erp_outside,
    parts: partsText(item.parts),
    remainderDate: item.remainder_date_manual ?? '',
    remainderInstrument: item.remainder_instrument ?? '',
    note: item.note ?? '',
  }
}

export function gridRowsFromCaris(caris: readonly CashPlanCari[]): PlanningGridRow[] {
  return caris.flatMap((cari) => cari.items.map((item) => gridRowFromItem(cari, item)))
}

const VALUE_FIELDS = [
  'klass',
  'erpOutside',
  'parts',
  'remainderDate',
  'remainderInstrument',
  'note',
] as const

/** Satır yüklenen hâlinden farklı mı. */
export function isRowDirty(row: PlanningGridRow, original: PlanningGridRow | undefined): boolean {
  if (!original) return false
  return VALUE_FIELDS.some((f) => row[f] !== original[f])
}

/** İstemci şeması: kısımlar metni çözülmeli, kalan tarihi GG.AA.YYYY, not ≤ 1000. */
export function planningGridSchema(instruments: readonly string[]) {
  return z
    .object({
      klass: z.string().refine((v) => CLASSES.includes(v), t.grid.classInvalid),
      erpOutside: z.boolean(),
      parts: z.string(),
      remainderDate: z.string(),
      remainderInstrument: z.string(),
      note: z.string().max(1000, t.noteTooLong),
    })
    .superRefine((row, ctx) => {
      const parsed = parseParts(row.parts, instruments)
      if (!parsed.ok) ctx.addIssue({ code: 'custom', path: ['parts'], message: parsed.message })
      if (row.remainderDate && !ISO_DAY.test(row.remainderDate)) {
        ctx.addIssue({ code: 'custom', path: ['remainderDate'], message: t.grid.dateInvalid })
      }
    })
}

/** Toplu kaydet gövdesinin satırı; doğrulanmış satırdan kurulur, okunan sürüm gider. */
export function bulkRow(row: PlanningGridRow, instruments: readonly string[]): CashPlanBulkRow {
  const parsed = parseParts(row.parts, instruments)
  return {
    key: row.key,
    version: row.version,
    klass: row.klass,
    erp_outside: row.erpOutside,
    parts: parsed.ok ? parsed.parts : [],
    remainder_date: row.remainderDate || null,
    remainder_instrument: row.remainderInstrument || null,
    note: row.note || null,
  }
}

function text<K extends 'parts' | 'note'>(field: K) {
  return {
    getText: (row: PlanningGridRow) => row[field],
    setText: (row: PlanningGridRow, value: string) => ({ ...row, [field]: value }),
  }
}

function readonly(getText: (row: PlanningGridRow) => string) {
  return { kind: 'readonly' as const, getText, setText: (row: PlanningGridRow) => row }
}

const money = (value: number) => formatDecimal(value, 2)

/**
 * Kolonlar; kimlik sabit kalsın diye ekran `useMemo` ile kurar. Kalan tarihi ve aracı yan yanadır:
 * Excel'den bu iki sütun birlikte yapıştırılır (S5).
 */
export function planningGridColumns(instruments: readonly string[]): GridColumn<PlanningGridRow>[] {
  const c = t.columns
  return [
    { id: 'cariCode', header: c.cariCode, width: 90, ...readonly((row) => row.cariCode) },
    { id: 'cariName', header: c.cariName, width: 180, ...readonly((row) => row.cariName) },
    {
      id: 'doc',
      header: t.doc,
      width: 120,
      ...readonly((row) => row.item.doc_no ?? row.key),
    },
    { id: 'type', header: t.type, width: 70, ...readonly((row) => row.item.type_code) },
    {
      id: 'due',
      header: t.due,
      width: 90,
      ...readonly((row) => formatDateOnly(row.item.due)),
    },
    {
      id: 'open',
      header: t.open,
      width: 110,
      align: 'right',
      ...readonly((row) => money(row.item.open_amount)),
    },
    {
      id: 'klass',
      header: t.klass,
      width: 90,
      kind: 'select',
      options: () => CLASSES.map((v) => ({ value: v, label: v })),
      getText: (row) => row.klass,
      setText: (row, value) => ({ ...row, klass: value }),
    },
    {
      id: 'erpOutside',
      header: c.erpOutside,
      width: 80,
      kind: 'select',
      options: () => [
        { value: 'true', label: t.grid.yes },
        { value: 'false', label: t.grid.no },
      ],
      getText: (row) => String(row.erpOutside),
      setText: (row, value) =>
        value === 'true' || value === 'false' ? { ...row, erpOutside: value === 'true' } : row,
    },
    { id: 'parts', header: t.parts, width: 260, kind: 'text', ...text('parts') },
    {
      id: 'remainder',
      header: t.remainder,
      width: 110,
      align: 'right',
      ...readonly((row) => money(row.item.remainder)),
    },
    {
      id: 'remainderDate',
      header: t.remainderDate,
      width: 100,
      kind: 'text',
      getText: (row) =>
        ISO_DAY.test(row.remainderDate) ? formatDateOnly(row.remainderDate) : row.remainderDate,
      setText: (row, value) => ({
        ...row,
        remainderDate: value.trim() ? (parseDay(value) ?? value) : '',
      }),
      render: (row) => {
        if (row.remainderDate) {
          return ISO_DAY.test(row.remainderDate)
            ? formatDateOnly(row.remainderDate)
            : row.remainderDate
        }
        // Elle girilmemiş: kuraldan hesaplanan tarih yer tutucudur, kaydedilmez (`bulkRow` boş gönderir).
        return row.item.remainder_date ? (
          <span className="text-text-secondary italic" title={t.grid.remainderRule}>
            {formatDateOnly(row.item.remainder_date)}
          </span>
        ) : (
          ''
        )
      },
    },
    {
      id: 'remainderInstrument',
      header: c.remainderInstrument,
      width: 130,
      kind: 'select',
      options: () => [
        { value: '', label: c.defaultInstrument },
        ...instruments.map((v) => ({ value: v, label: v })),
      ],
      getText: (row) => row.remainderInstrument,
      setText: (row, value) => ({ ...row, remainderInstrument: value }),
    },
    {
      id: 'control',
      header: c.control,
      width: 150,
      ...readonly((row) => (row.item.control ? (t.controls[row.item.control] ?? '') : '')),
    },
    { id: 'note', header: t.note, width: 180, kind: 'text', ...text('note') },
    { id: 'updated', header: c.updated, width: 170, ...readonly(updatedText) },
  ]
}

function updatedText(row: PlanningGridRow): string {
  const { updated_by_name: name, updated_at: at } = row.item
  if (!at) return ''
  return name ? t.lastChange(name, formatDateTime(at)) : formatDateTime(at)
}
